import React, { Fragment, useEffect, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { useMediaQuery } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useDataProvider } from 'react-admin';

import {
  FactCheck,
  Handyman,
  HomeRepairService,
  StickyNote2,
} from 'components/Icons';
import { PhaseTypeEnum, PhaseStructureTypeEnum } from '@vatos-pas/common';
import { getPhaseByType } from 'utils/phaseUtils';
import { LocationProvider } from 'providers/locationProvider';
import { MapProvider } from 'components/Map';
import { MasterSheetContainer } from '../containers';
import {
  getPhaseDisplayName,
  getModalConfig,
  BUMP_PHASES,
} from '../constants/phaseConfigurations';
import { useEditStyles } from './MasterSheetEditStyles';
import StatusBar from '../components/StatusBar';
import JobInfo from '../components/JobInfo';
import MobileJobInfo from '../components/MobileJobInfo';
import Boards from '../components/Boards';
import Timeline from '../components/Timeline';
import Notes from '../components/Notes';
import Repairs from '../components/Repairs';
import ArchiveJobButton from '../components/ArchiveJobButton';
import type { Job } from '../containers/useMasterSheetContainer';
import {
  WorkflowConfigProvider,
  useWorkflowConfig,
  DRYWALL_AND_PAINT_CONFIG,
} from '../config';

export enum TabEnum {
  Boards = 'boards',
  Timeline = 'timeline',
  Notes = 'notes',
  Repairs = 'repairs',
}

const getRepairsLot = async ({
  jobId,
  setJob,
  setLoadingRepairs,
  dataProvider,
}) => {
  const jobRepairsCall = await dataProvider.getList('job-lot-repair-report', {
    filter: {
      jobId,
    },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' },
  });
  const jobRepairs = jobRepairsCall.data.sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : 0,
  );

  setJob((current: Job | null) =>
    current && current.job.id === jobId
      ? {
          ...current,
          jobRepairs,
          repairFormula: [],
        }
      : current,
  );
  if (setLoadingRepairs) setLoadingRepairs(false);

  return jobRepairs;
};

const refreshRepairs =
  (setJob: any, theJob: any, setLoadingRepairs: any, dataProvider: any) =>
  async () => {
    return getRepairsLot({
      jobId: theJob?.job?.id,
      setJob,
      setLoadingRepairs,
      dataProvider,
    });
  };

const MasterSheetEditInner: React.FC = () => {
  const config = useWorkflowConfig();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const {
    selectedJob,
    loadJobById,
    updateJobHouseMarked,
    loadingJob: _loadingJob,
    jobLotError,
    setChangeTabCallback,
    setSelectedJob,
  } = MasterSheetContainer.useContainer();
  const isMobile = useMediaQuery(`(max-width:${config.edit.mobileBreakpoint})`);
  const classes = useEditStyles();
  const {
    coords: geoCoords,
    accuracy: geoAccuracy,
    error: geoError,
    loading: geoLoading,
    requestLocation,
    permissionState: geoPermissionState,
  } = LocationProvider.useContainer();
  const geoDisplayError =
    geoError ?? (!geoLoading && geoPermissionState === 'denied' ? 'You need to enable your location' : null);

  const [tabSelected, setTabSelected] = useState<TabEnum | null>(null);
  const [_loadingRepairs, setLoadingRepairs] = useState(false);
  const [initialRepairId, setInitialRepairId] = useState<string | undefined>();

  // Go Back returns to the list the job was opened from. Archiving (or
  // reactivating) the job on this screen must not change that destination,
  // so remember whether it was already archived when it was opened.
  const archivedWhenOpened = useRef<{ jobId: string; archived: boolean }>();
  useEffect(() => {
    const job = selectedJob?.job;
    if (!job?.id || archivedWhenOpened.current?.jobId === job.id) return;
    archivedWhenOpened.current = { jobId: job.id, archived: !!job.archived };
  }, [selectedJob]);

  // Load job data when component mounts or ID changes
  useEffect(() => {
    const loadJobData = async () => {
      const urlPath = window.location.pathname;
      const hashPath = window.location.hash;

      // Extract repairId from URL query parameters (works for both hash and regular URLs)
      let repairId: string | null = null;

      // Try to get from hash first (e.g., #/job-master-sheet-v2/id?repairId=xxx)
      if (hashPath.includes('?')) {
        const hashQueryString = hashPath.split('?')[1];
        const hashParams = new URLSearchParams(hashQueryString);
        repairId = hashParams.get('repairId');
      }

      // Fallback to regular search params
      if (!repairId) {
        const urlParams = new URLSearchParams(window.location.search);
        repairId = urlParams.get('repairId');
      }

      // Try URL patterns using config-driven route segment
      const routeSegment = config.edit.backUrl.substring(1);
      let jobIdMatch = urlPath.match(new RegExp(`/${routeSegment}/([^?]+)`));

      // If not found in pathname, try hash routing (react-admin style)
      if (!jobIdMatch && hashPath) {
        jobIdMatch = hashPath.match(new RegExp(`#/${routeSegment}/([^?]+)`));
      }

      // Also try warranty URL segment
      if (!jobIdMatch) {
        const wseg = config.edit.warrantyUrlSegment;
        jobIdMatch = urlPath.match(new RegExp(`/${wseg}/([^?]+)`));
        if (!jobIdMatch && hashPath) {
          jobIdMatch = hashPath.match(new RegExp(`#/${wseg}/([^?]+)`));
        }
      }

      // Fallback: try direct ID patterns
      if (!jobIdMatch) {
        jobIdMatch = urlPath.match(/\/(\d+)$/);
      }

      if (jobIdMatch && jobIdMatch[1]) {
        const jobId = jobIdMatch[1];
        try {
          await loadJobById(jobId);

          // If repairId is present in URL, switch to Repairs tab and pass to component
          if (repairId) {
            setInitialRepairId(repairId);
            setTabSelected(TabEnum.Repairs);
          }
        } catch (error) {
          console.error('Failed to load job:', error);
        }
      }
    };

    loadJobData();
  }, []); // Remove loadJobById dependency to avoid infinite loop

  const isInWarranty = () => {
    const priority = (selectedJob as any)?.job?.currentJobPhasePriority;
    if (!priority) return false;

    const isDrywallAndPaint =
      (selectedJob as any)?.job?.phaseStructureType ===
      PhaseStructureTypeEnum.DrywallAndPaint;

    if (isDrywallAndPaint) {
      return priority >= 1100; // A partir do FinalPaint
    } else {
      return priority >= 600; // A partir do Bump1 (Drywall normal)
    }
  };

  const checkHouse = async (checked: boolean) => {
    if (!selectedJob?.id) {
      console.error('No selected job to mark house');
      return;
    }

    await updateJobHouseMarked(selectedJob.id.toString(), checked);
  };

  const getNextStatusText = (theJob: any) => {
    if (!theJob || !theJob.job) {
      return 'Loading...';
    }

    // Get current phase priority
    const priority = theJob.job.currentJobPhasePriority || 100;

    // Handle Materials phase
    if (priority === 100) {
      return 'Materials Sign Off';
    }

    const currentPhaseType: PhaseTypeEnum | undefined =
      theJob.jobPhase?.phaseType;

    if (!currentPhaseType) {
      return 'Unknown Phase';
    }

    // Get display name for phase

    const phaseName = getPhaseDisplayName(currentPhaseType);

    // Get current phase data using getPhase function
    const currentPhase = getPhaseByType(theJob.jobPhases, currentPhaseType);

    // If phase not found, return a default status
    if (!currentPhase) {
      return `${phaseName}: Phase Not Available`;
    }

    // Check if this is a log phase
    const modalConfig = getModalConfig(currentPhaseType);
    const isLogPhase = modalConfig && modalConfig.modalType === 'log';

    // Handle phase status
    if (currentPhase.approved === true) {
      return `${phaseName}: Approved`;
    }

    if (currentPhase.requested === true) {
      if (currentPhase.contractorId) {
        return `${phaseName}: Resources Allocated`;
      }
      return `${phaseName}: Resources Requested`;
    } else {
      // For log phases, show "Log Contractor" — but bump phases in bumpout timesheet flow use "Request Resource"
      const isBumpPhase = currentPhaseType && BUMP_PHASES.includes(currentPhaseType);
      const isBumpoutActive = isBumpPhase && theJob?.job?.reqBumpoutTimesheet;
      const actionText = isLogPhase && !isBumpoutActive ? 'Log Contractor' : 'Request Resource';
      return `${phaseName}: ${actionText}`;
    }
  };

  const isWarrantyUrl =
    window.location.href.indexOf(config.edit.warrantyUrlSegment) !== -1;

  const handleGoBack = () => {
    if (archivedWhenOpened.current?.archived) {
      history.push('/job-archived-sheet');
    } else if (isWarrantyUrl) {
      history.push('/' + config.edit.warrantyUrlSegment);
    } else {
      history.push(config.edit.backUrl);
    }
  };

  // Control visibility of bump phases based on current job phase priority
  const showBump = (bumpType: PhaseTypeEnum) => {
    if (!selectedJob?.job?.currentJobPhasePriority) return false;

    // Original Drywall bumps (Bump4 and Bump5)
    if (bumpType === PhaseTypeEnum.Bump4) {
      return (
        selectedJob.job.currentJobPhasePriority > 800 ||
        (selectedJob.job.currentJobPhasePriority === 800 &&
          selectedJob.jobPhase?.approved)
      );
    }
    if (bumpType === PhaseTypeEnum.Bump5) {
      return (
        selectedJob.job.currentJobPhasePriority > 900 ||
        (selectedJob.job.currentJobPhasePriority === 900 &&
          selectedJob.jobPhase?.approved)
      );
    }

    // DrywallAndPaint bumps - only show after FinalPaint is approved (priority 1100)
    if (bumpType === PhaseTypeEnum.DrywallTouchUp3) {
      return (
        selectedJob.job.currentJobPhasePriority > 1100 ||
        (selectedJob.job.currentJobPhasePriority === 1100 &&
          selectedJob.jobPhase?.approved)
      );
    }
    if (bumpType === PhaseTypeEnum.CMBluetape) {
      return (
        selectedJob.job.currentJobPhasePriority > 1100 ||
        (selectedJob.job.currentJobPhasePriority === 1100 &&
          selectedJob.jobPhase?.approved)
      );
    }
    if (bumpType === PhaseTypeEnum.FinalBluetape) {
      return (
        selectedJob.job.currentJobPhasePriority > 1100 ||
        (selectedJob.job.currentJobPhasePriority === 1100 &&
          selectedJob.jobPhase?.approved)
      );
    }

    return false;
  };

  const onChangeTab = (newTab: TabEnum) => {
    setTabSelected(newTab);
  };

  // Register the change tab callback with the container
  useEffect(() => {
    setChangeTabCallback(() => (tab: string) => {
      setTabSelected(tab as TabEnum);
    });
  }, [setChangeTabCallback]);

  // Set initial tab based on job phase priority
  useEffect(() => {
    if (selectedJob && tabSelected === null) {
      const priority = selectedJob?.job?.currentJobPhasePriority || 0;

      if (priority > 100) {
        setTabSelected(TabEnum.Timeline);
      } else {
        setTabSelected(TabEnum.Boards);
      }
    }
  }, [selectedJob, tabSelected]);

  // Auto-load repairs when Repairs tab is selected
  // Uses `=== undefined` to distinguish "never fetched" from "fetched but empty []"
  useEffect(() => {
    if (
      tabSelected === TabEnum.Repairs &&
      selectedJob?.id &&
      (selectedJob as any)?.jobRepairs === undefined
    ) {
      getRepairsLot({
        jobId: selectedJob.id,
        setJob: setSelectedJob,
        setLoadingRepairs,
        dataProvider,
      });
    }
  }, [tabSelected, selectedJob?.id, dataProvider]);

  // Remove mock data initialization - we want to load real data from API

  // Check if we're at the root and need to redirect to job list
  const routeSegment = config.edit.backUrl.substring(1);
  const isAtRoot =
    window.location.pathname === '/' &&
    !window.location.hash.includes(routeSegment) &&
    !window.location.hash.includes(config.edit.warrantyUrlSegment);

  if (jobLotError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
        flexDirection="column"
      >
        <Typography>
          Error! Please verify if the link is correct or check your role and/or
          region.
        </Typography>
        <Button
          style={{ marginTop: '10px' }}
          fullWidth={false}
          variant="contained"
          color="primary"
          size="large"
          startIcon={<ChevronLeftIcon />}
          onClick={() => history.push(config.edit.backUrl)}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!selectedJob) {
    if (isAtRoot) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="h5" gutterBottom>
            Vatos PAS - Master Sheet V2
          </Typography>
          <Typography variant="body1" style={{ marginBottom: 20 }}>
            To access a specific job, use one of the URLs:
          </Typography>
          <Box textAlign="left">
            <Typography variant="body2">
              • {config.edit.backUrl}/[JOB_ID]
            </Typography>
            <Typography variant="body2">
              • #{config.edit.backUrl}/[JOB_ID]
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push(config.edit.backUrl)}
            style={{ marginTop: 20 }}
          >
            Go to Job List
          </Button>
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
        <Typography style={{ marginTop: 16 }}>Loading Job Info</Typography>
      </Box>
    );
  }

  return (
    <Fragment>
      <Box width="100%" maxWidth="100vw" overflow="hidden">
        <Box className={classes.createBox}>
          <Fragment>
            <Box
              mb={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<ChevronLeftIcon />}
                onClick={handleGoBack}
              >
                Go Back
              </Button>
              <ArchiveJobButton key={selectedJob.job.id} />
            </Box>
            {/* Status Bar */}
            {(config.edit.statusBarThreshold === 'gt200'
              ? (selectedJob?.job?.currentJobPhasePriority ?? 0) > 200
              : (selectedJob?.job?.currentJobPhasePriority ?? 0) >= 200) && (
              <StatusBar
                theJob={selectedJob}
                getPhase={getPhaseByType}
                showBump={showBump}
                isInWarranty={isInWarranty}
              />
            )}

            {/* Job Info */}
            {isMobile ? (
              <MobileJobInfo checkHouse={checkHouse} theJob={selectedJob} />
            ) : (
              <JobInfo checkHouse={checkHouse} theJob={selectedJob} />
            )}

            {/* Tabbed Content */}
            <Box className={classes.mobileBorderlessWhiteBox} overflow="auto">
              {tabSelected === null ? (
                <Box p={2}>
                  <Typography>Determining initial tab...</Typography>
                </Box>
              ) : (
                <>
                  <AppBar position="static">
                    <Tabs
                      value={tabSelected}
                      onChange={(_event, newTab) => onChangeTab(newTab)}
                      aria-label="Vatos Tabs on House Report"
                      centered
                      indicatorColor="secondary"
                      variant="fullWidth"
                      scrollButtons="auto"
                      classes={{
                        root: classes.tabs,
                        indicator: classes.indicator,
                      }}
                      style={{ backgroundColor: config.edit.tabBarColor }}
                    >
                      <Tab
                        label="Boards"
                        value={TabEnum.Boards}
                        className={classes.tabsText}
                        icon={isMobile ? <FactCheck /> : undefined}
                      />
                      <Tab
                        label="Timeline"
                        value={TabEnum.Timeline}
                        className={classes.tabsText}
                        icon={isMobile ? <Handyman /> : undefined}
                      />
                      <Tab
                        label="Notes"
                        value={TabEnum.Notes}
                        className={classes.tabsText}
                        icon={isMobile ? <StickyNote2 /> : undefined}
                      />
                      <Tab
                        label="Repairs"
                        value={TabEnum.Repairs}
                        className={classes.tabsText}
                        icon={isMobile ? <HomeRepairService /> : undefined}
                      />
                    </Tabs>
                  </AppBar>
                  <Box width="100%" overflow="auto">
                    {config.edit.boardsMountStrategy === 'keepMounted' ? (
                      <Box
                        display={
                          tabSelected === TabEnum.Boards ? 'block' : 'none'
                        }
                      >
                        <Boards geoLocationError={geoDisplayError ?? undefined} />
                      </Box>
                    ) : (
                      tabSelected === TabEnum.Boards && (
                        <Box>
                          <Boards geoLocationError={geoDisplayError ?? undefined} />
                        </Box>
                      )
                    )}

                    {tabSelected === TabEnum.Timeline && (
                      <Box>
                        <Timeline
                          geoCoords={geoCoords}
                          geoAccuracy={geoAccuracy}
                          geoLoading={geoLoading}
                          requestLocation={requestLocation}
                          geoLocationError={geoDisplayError ?? undefined}
                          showBump={showBump}
                          getNextStatusText={getNextStatusText}
                        />
                      </Box>
                    )}

                    {tabSelected === TabEnum.Notes && (
                      <Box>
                        <Notes />
                      </Box>
                    )}

                    {tabSelected === TabEnum.Repairs && (
                      <Box>
                        <Repairs
                          theJob={selectedJob}
                          refreshRepairs={refreshRepairs(
                            setSelectedJob,
                            selectedJob,
                            setLoadingRepairs,
                            dataProvider,
                          )}
                          setJob={setSelectedJob}
                          initialValue={initialRepairId}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Fragment>
        </Box>
      </Box>
    </Fragment>
  );
};

export const MasterSheetEdit: React.FC<any> = ({ config }) => {
  return (
    <WorkflowConfigProvider config={config ?? DRYWALL_AND_PAINT_CONFIG}>
      <MasterSheetContainer.Provider>
        <MapProvider>
          <MasterSheetEditInner />
        </MapProvider>
      </MasterSheetContainer.Provider>
    </WorkflowConfigProvider>
  );
};
